import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, ImagePicker, inputCls } from "./components";

const EMPTY = { title: "", tag: "News", date: "", text: "", img: "" };
const TAGS = ["News", "Community", "Season", "Program"];

export default function NewsSection() {
  const { items, loading, reload } = useResource("news");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!editing.title.trim() || !editing.text.trim()) {
      toast.error("Please enter a title and a description.");
      return;
    }
    setSaving(true);
    const dateStr = editing.date
      ? new Date(editing.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const payload = { title: editing.title.trim(), tag: editing.tag, date: dateStr, text: editing.text.trim(), img: editing.img || "" };
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, payload);
      else await api.post("/admin/news", payload);
      toast.success("Announcement saved! It's now live on the website.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the announcement.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/news/${deleting.id}`);
      toast.success("Announcement deleted.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the announcement.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-news-section">
      <SectionHeader
        title="News & Announcements"
        subtitle="Post updates for players, parents, and fans. They appear on the homepage as soon as you save."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-news-button">
            <Plus size={17} /> New Announcement
          </PrimaryBtn>
        }
      />

      <div className="space-y-4" data-testid="news-list">
        {items.length === 0 && <p className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">No announcements yet. Click "New Announcement".</p>}
        {items.map((n) => (
          <div key={n.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`news-row-${n.id}`}>
            {n.img && <img src={n.img} alt="" className="h-16 w-24 rounded-md object-cover shrink-0 hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="bg-[#0F4C2A]/10 text-[#0F4C2A] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">{n.tag}</span>
                <span className="text-xs text-slate-500">{n.date}</span>
              </div>
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate mt-1">{n.title}</p>
              <p className="text-xs text-slate-500 truncate">{n.text}</p>
            </div>
            <button onClick={() => setEditing({ ...n, date: "" })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit announcement" data-testid={`news-edit-${n.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(n)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete announcement" data-testid={`news-delete-${n.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Announcement" : "New Announcement"} onClose={() => setEditing(null)} testid="news-modal">
          <div className="space-y-4">
            <Field label="Title">
              <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Spring Tryouts Announced" data-testid="news-form-title" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category">
                <select className={inputCls} value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} data-testid="news-form-tag">
                  {TAGS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Date" hint="Leave blank to use today">
                <input type="date" className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="news-form-date" />
              </Field>
            </div>
            <Field label="Description">
              <textarea rows={4} className={inputCls} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="news-form-text" />
            </Field>
            <ImagePicker label="Photo (optional)" value={editing.img} onChange={(url) => setEditing({ ...editing, img: url })} testid="news-form-photo" />
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="news-form-save">
                {saving ? "Saving..." : "Save Announcement"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="news-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Delete the announcement "${deleting.title}"? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="news-delete-confirm"
        />
      )}
    </div>
  );
}
